import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Search, Home, Grid3x3, ShoppingBag, User, Menu, X, LogOut, Package, Heart, Sparkles } from "lucide-react";
import { useApp } from "@/context/AppContext";
import FloatingCallButton from "@/components/FloatingCallButton";
import ServiceSelectionSummary from "@/components/ServiceSelectionSummary";
import JourneyPopup from "@/components/JourneyPopup";
import LoginGate from "@/components/LoginGate";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/category/wedding", label: "Wedding" },
  { to: "/category/once_a_year", label: "Once A Year" },
  { to: "/category/tent_items", label: "Tent Items" },
  { to: "/venues", label: "Packages" },
  { to: "/create-event", label: "Create Your Event", highlight: true },
];

export default function Layout({ children }) {
  const { user, logout, selection, wishlist } = useApp();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const nav = useNavigate();
  const loc = useLocation();

  const submit = (e) => {
    e.preventDefault();
    if (q.trim()) nav(`/search?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <div className="min-h-screen bg-[#fcfaf8] flex flex-col">
      {/* Top nav */}
      <header data-testid="top-nav" className="sticky top-0 z-40 bg-white/85 backdrop-blur-md border-b border-[#ebe6e0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between gap-6">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-2.5 group">
            <span className="relative inline-flex items-center justify-center w-9 h-9 rounded-full border border-[#d4af37]/50 group-hover:border-[#d4af37] transition-colors">
              <svg viewBox="0 0 32 32" className="w-5 h-5" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M6 26V14a10 10 0 0 1 20 0v12" stroke="#d4af37" strokeWidth="1.5" strokeLinecap="round" />
                <path d="M16 4v3" stroke="#d4af37" strokeWidth="1.5" strokeLinecap="round" />
                <circle cx="16" cy="4" r="1.25" fill="#d4af37" />
                <path d="M12 26v-6a4 4 0 1 1 8 0v6" stroke="#d4af37" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </span>
            <span className="flex items-baseline gap-1.5 leading-none">
              <span className="font-serif text-xl tracking-tight text-[#292724]">Tent</span>
              <span className="font-serif text-xl text-[#d4af37] italic">N</span>
              <span className="font-serif text-xl tracking-tight text-[#292724]">Tap</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-6 whitespace-nowrap">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} data-testid={`nav-${n.label.replace(/\s/g,'-').toLowerCase()}`}
                className={`text-sm tracking-wide transition-colors ${n.highlight ? "px-4 py-1.5 rounded-full bg-[#d4af37] text-[#292724] hover:bg-[#c9a233] font-medium" : `hover:text-[#d4af37] ${loc.pathname === n.to ? "text-[#d4af37]" : "text-[#292724]"}`}`}>
                {n.label}
              </Link>
            ))}
          </nav>

          <form onSubmit={submit} className="hidden md:flex items-center bg-[#f4efeb] rounded-full px-4 py-2 w-56 xl:w-72">
            <Search className="w-4 h-4 text-[#8a837a]" />
            <input data-testid="search-input" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search decor, tents, packages..."
              className="bg-transparent ml-2 flex-1 text-sm placeholder:text-[#8a837a] focus:outline-none" />
          </form>

          <div className="hidden lg:flex items-center gap-4 whitespace-nowrap">
            <Link to="/wishlist" data-testid="nav-wishlist" className="relative p-2 hover:text-[#d4af37]">
              <Heart className="w-5 h-5" />
              {wishlist.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-[#d4af37] text-white text-[10px] rounded-full w-5 h-5 flex items-center justify-center">{wishlist.length}</span>
              )}
            </Link>
            <Link to="/selection" data-testid="nav-selection" className="relative p-2 hover:text-[#d4af37]">
              <ShoppingBag className="w-5 h-5" />
              {selection.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-[#d4af37] text-white text-[10px] rounded-full w-5 h-5 flex items-center justify-center">{selection.length}</span>
              )}
            </Link>
            {user ? (
              <div className="relative group">
                <button data-testid="user-menu" className="flex items-center gap-2 text-sm">
                  <User className="w-4 h-4" /> {(user.name || user.phone || "Account").split(" ")[0]}
                </button>
                <div className="absolute right-0 top-full mt-2 w-56 bg-white border border-[#ebe6e0] rounded-xl shadow-xl opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity">
                  <Link to="/my-bookings" data-testid="my-bookings-link" className="block px-4 py-3 text-sm hover:bg-[#f4efeb]">My Bookings</Link>
                  <Link to="/account" data-testid="account-link" className="block px-4 py-3 text-sm hover:bg-[#f4efeb]">Account</Link>
                  {user.is_admin && <Link to="/admin" data-testid="admin-link" className="block px-4 py-3 text-sm hover:bg-[#f4efeb]">Admin Dashboard</Link>}
                  <button onClick={logout} data-testid="logout-btn" className="w-full text-left px-4 py-3 text-sm hover:bg-[#f4efeb] flex items-center gap-2"><LogOut className="w-4 h-4" /> Logout</button>
                </div>
              </div>
            ) : (
              <Link to="/login" data-testid="nav-login" className="btn-outline-gold text-sm !py-2 !px-5">Sign In</Link>
            )}
          </div>

          <button data-testid="mobile-menu-btn" onClick={() => setOpen(!open)} className="lg:hidden p-2">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden bg-white border-t border-[#ebe6e0] px-6 py-4 space-y-3">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className="block py-2 text-[#292724]">{n.label}</Link>
            ))}
            <Link to="/selection" data-testid="mobile-menu-selection" onClick={() => setOpen(false)} className="flex items-center gap-2 py-2 text-[#292724]">
              <ShoppingBag className="w-4 h-4" /> Selection
              {selection.length > 0 && <span className="ml-1 bg-[#d4af37] text-white text-[10px] rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">{selection.length}</span>}
            </Link>
            {user ? (
              <>
                <Link to="/wishlist" onClick={() => setOpen(false)} className="block py-2">Wishlist ({wishlist.length})</Link>
                <Link to="/my-bookings" onClick={() => setOpen(false)} className="block py-2">My Bookings</Link>
                <Link to="/account" onClick={() => setOpen(false)} className="block py-2">Account</Link>
                {user.is_admin && <Link to="/admin" onClick={() => setOpen(false)} className="block py-2">Admin</Link>}
                <button onClick={() => { logout(); setOpen(false); }} className="block py-2 text-left">Logout</button>
              </>
            ) : (
              <Link to="/login" onClick={() => setOpen(false)} className="block py-2">Sign In</Link>
            )}
          </div>
        )}
      </header>

      <main className="flex-1 pb-24 lg:pb-0">{children}</main>
      <ServiceSelectionSummary />
      <JourneyPopup />
      <LoginGate />

      {/* Mobile bottom nav */}
      <nav data-testid="mobile-bottom-nav" className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur-xl border-t border-[#ebe6e0] h-16 flex justify-around items-center">        <Link to="/" className="flex flex-col items-center text-xs gap-1 text-[#292724]"><Home className="w-5 h-5" /> Home</Link>
        <Link to="/category/wedding" className="flex flex-col items-center text-xs gap-1 text-[#292724]"><Grid3x3 className="w-5 h-5" /> Categories</Link>
        <Link to="/create-event" data-testid="mobile-nav-create-event" className="flex flex-col items-center text-xs gap-0.5 text-[#292724] -mt-6">
          <span className="w-11 h-11 rounded-full bg-[#d4af37] text-[#292724] flex items-center justify-center shadow-[0_8px_20px_rgba(212,175,55,0.45)]"><Sparkles className="w-5 h-5" /></span>
          Create
        </Link>
        <Link to="/search" className="flex flex-col items-center text-xs gap-1 text-[#292724]"><Search className="w-5 h-5" /> Search</Link>
        <Link to={user ? "/account" : "/login"} className="flex flex-col items-center text-xs gap-1 text-[#292724]"><User className="w-5 h-5" /> Account</Link>
      </nav>

      {/* Footer */}
      <footer className="hidden lg:block bg-[#292724] text-[#f4efeb] mt-20">
        <div className="max-w-7xl mx-auto px-8 py-16 grid grid-cols-4 gap-12">
          <div>
            <div className="flex items-center gap-2.5 mb-3">
              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-[#d4af37]/60">
                <svg viewBox="0 0 32 32" className="w-4 h-4" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M6 26V14a10 10 0 0 1 20 0v12" stroke="#d4af37" strokeWidth="1.5" strokeLinecap="round" />
                  <circle cx="16" cy="4" r="1.25" fill="#d4af37" />
                  <path d="M12 26v-6a4 4 0 1 1 8 0v6" stroke="#d4af37" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </span>
              <div className="font-serif text-2xl">Tent <span className="text-[#d4af37] italic">N</span> Tap</div>
            </div>
            <p className="text-sm text-[#a8a29c] leading-relaxed">Luxury décor, event essentials and curated experiences — all in one place.</p>
          </div>
          <div>
            <div className="eyebrow mb-3">Shop</div>
            <ul className="space-y-2 text-sm">
              <li><Link to="/category/wedding" className="hover:text-[#d4af37]">Wedding</Link></li>
              <li><Link to="/category/once_a_year" className="hover:text-[#d4af37]">Once A Year</Link></li>
              <li><Link to="/category/tent_items" className="hover:text-[#d4af37]">Tent Items</Link></li>
              <li><Link to="/venues" className="hover:text-[#d4af37]">Packages</Link></li>
            </ul>
          </div>
          <div>
            <div className="eyebrow mb-3">Support</div>
            <ul className="space-y-2 text-sm">
              <li><a href={`https://wa.me/917297801755`} className="hover:text-[#d4af37]">WhatsApp: +91 72978 01755</a></li>
              <li>hello@tentntap.com</li>
              <li>Jaipur · Pushkar · Udaipur · Jaisalmer · Ranthambore</li>
            </ul>
          </div>
          <div>
            <div className="eyebrow mb-3">Curate</div>
            <p className="text-sm text-[#a8a29c]">Share your guest count, city and event date. Our décor curators craft the rest — free consultation, on WhatsApp.</p>
          </div>
        </div>
        <div className="border-t border-white/10 py-6 text-center text-xs text-[#a8a29c]">© 2026 Tent N Tap · Curated for Indian luxury weddings</div>
      </footer>

      <FloatingCallButton />
    </div>
  );
}
